import React, { useRef, useState, useCallback } from 'react';
import { Reorder, useDragControls, useAnimation } from 'framer-motion';

export default function LongPressReorderItem({ inTelegram: _inTelegram, children, onDragStart, onDragEnd, onPointerDown: externalPointerDown, disableReorder = false, ...props }) {
  const dragControls = useDragControls();
  const animationControls = useAnimation();
  const timerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const activatedRef = useRef(false);
  const touchMoveListenerOptions = useRef({ passive: false });
  // Prevents the page from scrolling while dragging on touch devices
  const preventScroll = useCallback((e) => {
    if (e.cancelable) {
      e.preventDefault();
    }
  }, []);

  const handlePointerDown = (e) => {
    if (externalPointerDown) externalPointerDown(e);
    if (disableReorder) return;
    if (e.persist) e.persist();
    activatedRef.current = false;
    const startX = e.clientX;
    const startY = e.clientY;
    const startDrag = () => {
      activatedRef.current = true;
      // Block scrolling immediately, before framer-motion onDragStart fires
      document.addEventListener('touchmove', preventScroll, touchMoveListenerOptions.current);
      const { body, documentElement } = document;
      if (body) body.style.overflow = 'hidden';
      if (documentElement) documentElement.style.overflow = 'hidden';
      if (window.Telegram?.WebApp?.disableVerticalSwipes) {
        try { window.Telegram.WebApp.disableVerticalSwipes(); } catch (_) {}
      }
      if (e.preventDefault) e.preventDefault();
      dragControls.start(e);
      animationControls.start({
        scale: [1, 1.1, 1.03],
        transition: { duration: 0.25 },
      });
    };
    timerRef.current = setTimeout(startDrag, 300);
    const onMove = (moveEvent) => {
      // Allow small finger wobble (10px threshold) on touch devices
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (dx * dx + dy * dy > 100) {
        cancel();
      }
    };
    const cancel = () => {
      clearTimeout(timerRef.current);
      window.removeEventListener('pointerup', cancel);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('pointermove', onMove);
    };
    window.addEventListener('pointerup', cancel);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('pointermove', onMove);
  };

  const handleDragStart = () => {
    setIsDragging(true);
    onDragStart && onDragStart();
    if (typeof window !== 'undefined') {
      const { body, documentElement } = document;
      if (window.Telegram?.WebApp?.disableVerticalSwipes) {
        try {
          window.Telegram.WebApp.disableVerticalSwipes();
        } catch (e) {}
      }
      if (body) body.style.overflow = 'hidden';
      if (documentElement) documentElement.style.overflow = 'hidden';
      // touchmove prevention is already added in startDrag (handlePointerDown),
      // but ensure it's there in case onDragStart fires without our long-press path
      if (!activatedRef.current) {
        document.addEventListener('touchmove', preventScroll, touchMoveListenerOptions.current);
      }
    }
  };

  const handleDragEnd = () => {
    if (typeof window !== 'undefined') {
      const { body, documentElement } = document;
      if (window.Telegram?.WebApp?.enableVerticalSwipes) {
        try {
          window.Telegram.WebApp.enableVerticalSwipes();
        } catch (e) {}
      }
      if (body) body.style.overflow = '';
      if (documentElement) documentElement.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll, touchMoveListenerOptions.current);
    }
    setIsDragging(false);
    onDragEnd && onDragEnd();
    animationControls.start({ scale: 1, transition: { duration: 0.2 } });
  };

  return (
    <Reorder.Item
      drag="y"
      dragListener={false}
      dragControls={dragControls}
      onPointerDown={handlePointerDown}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      whileDrag={{ scale: 1.03 }}
      animate={animationControls}
      style={{ touchAction: isDragging ? 'none' : 'pan-y' }}
      {...props}
    >
      {children}
    </Reorder.Item>
  );
}
